import { AfterViewInit, Component, ElementRef, Input, OnChanges, OnDestroy, OnInit, SimpleChanges, ViewChild, ViewEncapsulation } from '@angular/core';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { Chart, ChartConfiguration, registerables } from 'chart.js';
import { EstudioCientificoService } from 'src/app/core/services/estudio-cientifico/estudio-cientifico.service';
import { MODAL_DIALOG_TYPES } from 'src/app/core/utils/modal-dialog-types';
import { DashboardProfesorModalComponent } from './dashboard-profesor-modal.component';

Chart.register(...registerables);

export type DashboardLevel = 'center' | 'group' | 'teacher' | 'teacherCenter' | 'global';
export type ActionsTab = 'porTipo' | 'topProfes' | 'timeline';
export type TeachersSortColumn = 'teacherName' | 'centerName' | 'numDesafios' | 'sesionesEditor' | 'sesionesProfesor' | 'tiempoTotalMs' | 'totalAcciones';
export type SesionesSortColumn = 'teacherName' | 'fecha' | 'horaInicio' | 'horaFin' | 'duracionMs' | 'role' | 'accion' | 'groupTitle';
export type DesafiosSortColumn = 'teacherName' | 'groupTitle' | 'nombreSesion' | 'ideografoNombre' | 'courseTitle' | 'totalPreguntas' | 'aciertos' | 'fallos' | 'successRate';
type SortDir = 'asc' | 'desc';

@Component({
	selector: 'app-dashboard-estudio',
	templateUrl: './dashboard-estudio.component.html',
	styleUrls: ['./dashboard-estudio.component.scss'],
	encapsulation: ViewEncapsulation.None,
	standalone: false
})
export class DashboardEstudioComponent implements OnInit, OnChanges, OnDestroy, AfterViewInit {

	@Input() level: DashboardLevel = 'group';
	@Input() idCenter: number = 0;
	@Input() idGroup: number = 0;
	@Input() idUser: number = 0;
	@Input() autoLoad: boolean = true;

	@ViewChild('activityChart') activityChartRef: ElementRef<HTMLCanvasElement>;
	@ViewChild('rolePieChart') rolePieChartRef: ElementRef<HTMLCanvasElement>;
	@ViewChild('actionsByTypeChart') actionsByTypeChartRef: ElementRef<HTMLCanvasElement>;
	@ViewChild('topTeachersChart') topTeachersChartRef: ElementRef<HTMLCanvasElement>;
	@ViewChild('actionsTimelineChart') actionsTimelineChartRef: ElementRef<HTMLCanvasElement>;

	dashboardData: any = null;
	loading: boolean = false;
	period: string = 'all';

	periods = [
		{ key: 'today', label: 'Hoy' },
		{ key: 'week', label: 'Semana' },
		{ key: 'month', label: 'Mes' },
		{ key: 'all', label: 'Todo' }
	];

	activeActionsTab: ActionsTab = 'porTipo';

	teachersSortColumn: TeachersSortColumn = 'totalAcciones';
	teachersSortDir: 'asc' | 'desc' = 'desc';

	searchSesiones: string = '';
	searchDesafios: string = '';

	sesionesSortColumn: SesionesSortColumn | null = null;
	sesionesSortDir: SortDir = 'desc';

	desafiosSortColumn: DesafiosSortColumn | null = null;
	desafiosSortDir: SortDir = 'desc';

	private activityChart: Chart | null = null;
	private rolePieChart: Chart | null = null;
	private actionsByTypeChart: Chart | null = null;
	private topTeachersChart: Chart | null = null;
	private actionsTimelineChart: Chart | null = null;
	private viewReady: boolean = false;

	constructor(private estudioService: EstudioCientificoService, private modalService: NgbModal) {}

	ngOnInit() {
		if (this.autoLoad) this.loadDashboard();
	}

	ngAfterViewInit() {
		this.viewReady = true;
		if (this.dashboardData) {
			setTimeout(() => this.renderCharts(), 0);
		}
	}

	ngOnChanges(changes: SimpleChanges) {
		if (!this.autoLoad) return;
		if (changes['idCenter'] || changes['idGroup'] || changes['idUser'] || changes['level']) {
			if (changes['level'] && changes['level'].firstChange) return;
			this.loadDashboard();
		}
	}

	ngOnDestroy() {
		this.destroyCharts();
	}

	setPeriod(p: string) {
		if (this.period === p) return;
		this.period = p;
		this.loadDashboard();
	}

	loadDashboard() {
		this.loading = true;
		let obs;
		switch (this.level) {
			case 'teacher':
				obs = this.estudioService.getDashboardTeacher(this.idGroup, this.idUser, this.period);
				break;
			case 'teacherCenter':
				obs = this.estudioService.getDashboardTeacherInCenter(this.idCenter, this.idUser, this.period);
				break;
			case 'group':
				obs = this.estudioService.getDashboardGroup(this.idGroup, this.period);
				break;
			case 'center':
				obs = this.estudioService.getDashboardCenter(this.idCenter, this.period);
				break;
			case 'global':
				obs = this.estudioService.getDashboardGlobal(this.period);
				break;
		}
		obs.subscribe({
			next: (res) => {
				this.dashboardData = res?.data ?? res;
				this.loading = false;
				if (this.viewReady) {
					setTimeout(() => this.renderCharts(), 0);
				}
			},
			error: (err) => {
				console.error('[DashboardEstudio] Error:', err);
				this.loading = false;
			}
		});
	}

	formatTime(ms: number): string {
		if (!ms || ms <= 0) return '0m';
		const totalSec = Math.floor(ms / 1000);
		const h = Math.floor(totalSec / 3600);
		const m = Math.floor((totalSec % 3600) / 60);
		if (h > 0) return `${h}h ${m}m`;
		return `${m}m`;
	}

	getRolBadgeClass(role: string): string {
		if (!role) return 'role-default';
		const r = role.toUpperCase();
		if (r.includes('PROFESOR') || r.includes('TEACHER')) return 'role-profesor';
		if (r.includes('EDITOR') || r.includes('AUTOR') || r.includes('AUTHOR')) return 'role-editor';
		return 'role-default';
	}

	getSuccessRate(d: any): number {
		const total = (d?.aciertos || 0) + (d?.fallos || 0);
		if (total === 0) return 0;
		return Math.round(((d?.aciertos || 0) / total) * 100);
	}

	getGlobalSuccessRate(): number {
		const aciertos = this.dashboardData?.totalAciertos || 0;
		const fallos = this.dashboardData?.totalFallos || 0;
		const total = aciertos + fallos;
		if (total === 0) return 0;
		return Math.round((aciertos / total) * 100);
	}

	setActionsTab(t: ActionsTab) {
		if (this.activeActionsTab === t) return;
		this.activeActionsTab = t;
		setTimeout(() => this.renderActiveActionsChart(), 0);
	}

	sortTeachers(col: TeachersSortColumn) {
		if (this.teachersSortColumn === col) {
			this.teachersSortDir = this.teachersSortDir === 'asc' ? 'desc' : 'asc';
		} else {
			this.teachersSortColumn = col;
			this.teachersSortDir = (col === 'teacherName' || col === 'centerName') ? 'asc' : 'desc';
		}
	}

	getTeachersSortIcon(col: TeachersSortColumn): string {
		if (this.teachersSortColumn !== col) return '↕';
		return this.teachersSortDir === 'asc' ? '↑' : '↓';
	}

	verDashboardProfesorEnCentro(p: any) {
		const idUser = p?.idUser;
		const idCenter = p?.idCenter;
		if (!idUser || !idCenter) return;
		const ref = this.modalService.open(DashboardProfesorModalComponent, {
			scrollable: true,
			windowClass: `${MODAL_DIALOG_TYPES.W90} h-100`,
		});
		ref.componentInstance.idCenter = idCenter;
		ref.componentInstance.idUser = idUser;
		ref.componentInstance.title = p?.teacherName || `Usuario ${idUser}`;
		ref.componentInstance.subtitle = p?.centerName || '';
	}

	get sortedTeachers(): any[] {
		const list: any[] = this.dashboardData?.topProfesores || [];
		const dir = this.teachersSortDir === 'asc' ? 1 : -1;
		const col = this.teachersSortColumn;
		return [...list].sort((a, b) => {
			const va = a?.[col];
			const vb = b?.[col];
			if (typeof va === 'number' && typeof vb === 'number') {
				return (va - vb) * dir;
			}
			const sa = (va ?? '').toString().toLowerCase();
			const sb = (vb ?? '').toString().toLowerCase();
			if (sa < sb) return -1 * dir;
			if (sa > sb) return 1 * dir;
			return 0;
		});
	}

	get sesionesRecientesFiltradas(): any[] {
		const list: any[] = this.dashboardData?.sesionesRecientes || [];
		const term = (this.searchSesiones || '').toLowerCase().trim();
		const filtered = !term ? [...list] : list.filter(s =>
			(s?.teacherName ?? '').toString().toLowerCase().includes(term) ||
			(s?.groupTitle ?? '').toString().toLowerCase().includes(term) ||
			(s?.role ?? '').toString().toLowerCase().includes(term) ||
			(s?.accion ?? '').toString().toLowerCase().includes(term) ||
			this.formatDateForSearch(s?.fecha).includes(term)
		);
		const col = this.sesionesSortColumn;
		if (!col) return filtered;
		const dir = this.sesionesSortDir === 'asc' ? 1 : -1;
		const numericCols: SesionesSortColumn[] = ['duracionMs'];
		const dateCols: SesionesSortColumn[] = ['fecha', 'horaInicio', 'horaFin'];
		return filtered.sort((a, b) => {
			if (numericCols.includes(col)) {
				return ((a?.[col] || 0) - (b?.[col] || 0)) * dir;
			}
			if (dateCols.includes(col)) {
				const da = a?.[col] ? new Date(a[col]).getTime() : 0;
				const db = b?.[col] ? new Date(b[col]).getTime() : 0;
				return (da - db) * dir;
			}
			const sa = (a?.[col] ?? '').toString().toLowerCase();
			const sb = (b?.[col] ?? '').toString().toLowerCase();
			if (sa < sb) return -1 * dir;
			if (sa > sb) return 1 * dir;
			return 0;
		});
	}

	get desafiosFiltrados(): any[] {
		const list: any[] = this.dashboardData?.desafios || [];
		const term = (this.searchDesafios || '').toLowerCase().trim();
		const filtered = !term ? [...list] : list.filter(d =>
			(d?.teacherName ?? '').toString().toLowerCase().includes(term) ||
			(d?.groupTitle ?? '').toString().toLowerCase().includes(term) ||
			(d?.nombreSesion ?? '').toString().toLowerCase().includes(term) ||
			(d?.ideografoNombre ?? '').toString().toLowerCase().includes(term) ||
			(d?.courseTitle ?? '').toString().toLowerCase().includes(term)
		);
		const col = this.desafiosSortColumn;
		if (!col) return filtered;
		const dir = this.desafiosSortDir === 'asc' ? 1 : -1;
		const numericCols: DesafiosSortColumn[] = ['totalPreguntas', 'aciertos', 'fallos', 'successRate'];
		return filtered.sort((a, b) => {
			if (col === 'successRate') {
				return (this.getSuccessRate(a) - this.getSuccessRate(b)) * dir;
			}
			if (numericCols.includes(col)) {
				return ((a?.[col] || 0) - (b?.[col] || 0)) * dir;
			}
			const sa = (a?.[col] ?? '').toString().toLowerCase();
			const sb = (b?.[col] ?? '').toString().toLowerCase();
			if (sa < sb) return -1 * dir;
			if (sa > sb) return 1 * dir;
			return 0;
		});
	}

	sortSesiones(col: SesionesSortColumn) {
		if (this.sesionesSortColumn === col) {
			this.sesionesSortDir = this.sesionesSortDir === 'asc' ? 'desc' : 'asc';
		} else {
			this.sesionesSortColumn = col;
			const stringCols: SesionesSortColumn[] = ['teacherName', 'role', 'accion', 'groupTitle'];
			this.sesionesSortDir = stringCols.includes(col) ? 'asc' : 'desc';
		}
	}

	getSesionesSortIcon(col: SesionesSortColumn): string {
		if (this.sesionesSortColumn !== col) return '↕';
		return this.sesionesSortDir === 'asc' ? '↑' : '↓';
	}

	sortDesafios(col: DesafiosSortColumn) {
		if (this.desafiosSortColumn === col) {
			this.desafiosSortDir = this.desafiosSortDir === 'asc' ? 'desc' : 'asc';
		} else {
			this.desafiosSortColumn = col;
			const stringCols: DesafiosSortColumn[] = ['teacherName', 'groupTitle', 'nombreSesion', 'ideografoNombre', 'courseTitle'];
			this.desafiosSortDir = stringCols.includes(col) ? 'asc' : 'desc';
		}
	}

	getDesafiosSortIcon(col: DesafiosSortColumn): string {
		if (this.desafiosSortColumn !== col) return '↕';
		return this.desafiosSortDir === 'asc' ? '↑' : '↓';
	}

	private formatDateForSearch(value: any): string {
		if (!value) return '';
		const d = value instanceof Date ? value : new Date(value);
		if (isNaN(d.getTime())) return '';
		const day = String(d.getDate()).padStart(2, '0');
		const month = String(d.getMonth() + 1).padStart(2, '0');
		const year = d.getFullYear();
		return `${day}/${month}/${year}`;
	}

	private destroyCharts() {
		if (this.activityChart) { this.activityChart.destroy(); this.activityChart = null; }
		if (this.rolePieChart) { this.rolePieChart.destroy(); this.rolePieChart = null; }
		if (this.actionsByTypeChart) { this.actionsByTypeChart.destroy(); this.actionsByTypeChart = null; }
		if (this.topTeachersChart) { this.topTeachersChart.destroy(); this.topTeachersChart = null; }
		if (this.actionsTimelineChart) { this.actionsTimelineChart.destroy(); this.actionsTimelineChart = null; }
	}

	private renderCharts() {
		this.destroyCharts();
		this.renderActivityChart();
		this.renderRolePieChart();
		if (this.level === 'global') {
			this.renderActiveActionsChart();
		}
	}

	private renderActiveActionsChart() {
		if (this.actionsByTypeChart) { this.actionsByTypeChart.destroy(); this.actionsByTypeChart = null; }
		if (this.topTeachersChart) { this.topTeachersChart.destroy(); this.topTeachersChart = null; }
		if (this.actionsTimelineChart) { this.actionsTimelineChart.destroy(); this.actionsTimelineChart = null; }

		switch (this.activeActionsTab) {
			case 'porTipo':
				this.renderActionsByTypeChart();
				break;
			case 'topProfes':
				this.renderTopTeachersChart();
				break;
			case 'timeline':
				this.renderActionsTimelineChart();
				break;
		}
	}

	private renderActivityChart() {
		if (!this.activityChartRef?.nativeElement || !this.dashboardData) return;
		const data = this.dashboardData.actividadDiaria || [];
		const labels = data.map((d: any) => d.fecha);
		const values = data.map((d: any) => Math.round((d.duracionMs || 0) / 60000));

		const config: ChartConfiguration = {
			type: 'bar',
			data: {
				labels: labels,
				datasets: [{
					label: 'Minutos de actividad',
					data: values,
					backgroundColor: 'rgba(51, 102, 255, 0.7)',
					borderColor: 'rgba(51, 102, 255, 1)',
					borderWidth: 1,
					borderRadius: 4
				}]
			},
			options: {
				responsive: true,
				maintainAspectRatio: false,
				plugins: {
					legend: { display: false },
					tooltip: {
						callbacks: {
							label: (ctx) => `${ctx.parsed.y} minutos`
						}
					}
				},
				scales: {
					y: {
						beginAtZero: true,
						title: { display: true, text: 'Minutos' }
					}
				}
			}
		};

		this.activityChart = new Chart(this.activityChartRef.nativeElement, config);
	}

	private renderRolePieChart() {
		if (!this.rolePieChartRef?.nativeElement || !this.dashboardData) return;
		const profesor = this.dashboardData.sesionesProfesor || 0;
		const editor = this.dashboardData.sesionesEditor || 0;
		const otros = Math.max(0, (this.dashboardData.totalSesiones || 0) - profesor - editor);

		const config: ChartConfiguration = {
			type: 'doughnut',
			data: {
				labels: ['Profesor', 'Editor', 'Otros'],
				datasets: [{
					data: [profesor, editor, otros],
					backgroundColor: [
						'rgba(51, 102, 255, 0.85)',
						'rgba(255, 159, 64, 0.85)',
						'rgba(150, 150, 150, 0.6)'
					],
					borderWidth: 1
				}]
			},
			options: {
				responsive: true,
				maintainAspectRatio: false,
				plugins: {
					legend: { position: 'bottom' }
				}
			}
		};

		this.rolePieChart = new Chart(this.rolePieChartRef.nativeElement, config);
	}

	private renderActionsByTypeChart() {
		if (!this.actionsByTypeChartRef?.nativeElement || !this.dashboardData) return;
		const data = this.dashboardData.accionesPorTipo || [];
		const labels = data.map((d: any) => d.action);
		const values = data.map((d: any) => d.count);

		const config: ChartConfiguration = {
			type: 'bar',
			data: {
				labels: labels,
				datasets: [{
					label: 'Nº de acciones',
					data: values,
					backgroundColor: 'rgba(51, 102, 255, 0.7)',
					borderColor: 'rgba(51, 102, 255, 1)',
					borderWidth: 1,
					borderRadius: 4
				}]
			},
			options: {
				responsive: true,
				maintainAspectRatio: false,
				indexAxis: 'y',
				plugins: { legend: { display: false } },
				scales: {
					x: { beginAtZero: true, title: { display: true, text: 'Nº acciones' } }
				}
			}
		};

		this.actionsByTypeChart = new Chart(this.actionsByTypeChartRef.nativeElement, config);
	}

	private renderTopTeachersChart() {
		if (!this.topTeachersChartRef?.nativeElement || !this.dashboardData) return;
		const all = this.dashboardData.topProfesores || [];
		const top = [...all]
			.sort((a: any, b: any) => (b.totalAcciones || 0) - (a.totalAcciones || 0))
			.slice(0, 20);
		const labels = top.map((p: any) => p.teacherName || `Usuario ${p.idUser}`);
		const values = top.map((p: any) => p.totalAcciones || 0);

		const config: ChartConfiguration = {
			type: 'bar',
			data: {
				labels: labels,
				datasets: [{
					label: 'Acciones totales',
					data: values,
					backgroundColor: 'rgba(255, 159, 64, 0.75)',
					borderColor: 'rgba(255, 159, 64, 1)',
					borderWidth: 1,
					borderRadius: 4
				}]
			},
			options: {
				responsive: true,
				maintainAspectRatio: false,
				indexAxis: 'y',
				plugins: { legend: { display: false } },
				scales: {
					x: { beginAtZero: true, title: { display: true, text: 'Acciones' } }
				}
			}
		};

		this.topTeachersChart = new Chart(this.topTeachersChartRef.nativeElement, config);
	}

	private renderActionsTimelineChart() {
		if (!this.actionsTimelineChartRef?.nativeElement || !this.dashboardData) return;
		const data = this.dashboardData.accionesDiarias || [];
		const labels = data.map((d: any) => d.fecha);
		const values = data.map((d: any) => d.duracionMs || 0); // en este caso es count, ver backend

		const config: ChartConfiguration = {
			type: 'line',
			data: {
				labels: labels,
				datasets: [{
					label: 'Acciones por día',
					data: values,
					backgroundColor: 'rgba(51, 102, 255, 0.2)',
					borderColor: 'rgba(51, 102, 255, 1)',
					borderWidth: 2,
					tension: 0.3,
					fill: true,
					pointRadius: 3
				}]
			},
			options: {
				responsive: true,
				maintainAspectRatio: false,
				plugins: { legend: { display: false } },
				scales: {
					y: { beginAtZero: true, title: { display: true, text: 'Nº acciones' } }
				}
			}
		};

		this.actionsTimelineChart = new Chart(this.actionsTimelineChartRef.nativeElement, config);
	}
}
